import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import {
  FiEdit2, FiMapPin, FiUsers,
  FiCalendar, FiAlertTriangle, FiRefreshCw,
  FiActivity, FiGift, FiCheckCircle, FiClock,
} from 'react-icons/fi';
import {
  getDerasar, updateDerasar, approveDerasarMember,
  getUserProfile, updateUserProfile, getDashboard,
} from '../../services/api';
import { StateCitySelect } from '../../components/common';
import { useAuth } from '../../context/AuthContext';
import { fmtCurrency } from '../../utils/formatNumber';
import styles from './MyDerasar.module.css';

// -- Pure helpers --------------------------------------------------------------
const formatDate = (d) =>
  d ? new Date(d).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' }) : '—';

const getDaysUntilExpiry = (endDate) => {
  if (!endDate) return null;
  return Math.ceil((new Date(endDate) - new Date()) / (1000 * 60 * 60 * 24));
};

const subBadgeClass = (s) => {
  if (s === 'active') return styles.subActive;
  if (s === 'expired') return styles.subExpired;
  if (s === 'pending_renewal') return styles.subRenewal;
  return styles.subPending;
};
const subLabel = { active: 'Active', expired: 'Expired', pending: 'Pending', pending_renewal: 'Renewal Requested' };

// -- AlertBanners --------------------------------------------------------------
const AlertBanners = ({ derasar, days, expiringSoon, isExpired, onRenewalRequest, renewLoading }) => (
  <>
    {isExpired && (
      <div className={styles.alertBanner}>
        <FiAlertTriangle size={16} />
        <div>
          <div className={styles.alertTitle}>Subscription Expired</div>
          <div className={styles.alertBody}>
            Your subscription expired on {formatDate(derasar.endDate)}.
            {derasar.paymentMode === 'trial' ? null : derasar.subscriptionStatus !== 'pending_renewal' ? (
              <button className={`btn btn-primary btn-sm ${styles.alertBtn}`} onClick={onRenewalRequest} disabled={renewLoading}>
                <FiRefreshCw size={12} /> {renewLoading ? 'Opening...' : 'Renew Subscription'}
              </button>
            ) : (
              <span className={styles.pendingRenewal}> Renewal payment submitted - awaiting super admin approval.</span>
            )}
          </div>
        </div>
      </div>
    )}
    {expiringSoon && !isExpired && (
      <div className={styles.warnBanner}>
        <FiAlertTriangle size={15} />
        Subscription expires in <strong>{days} day{days !== 1 ? 's' : ''}</strong> on {formatDate(derasar.endDate)}.
        {derasar.paymentMode !== 'trial' && derasar.subscriptionStatus !== 'pending_renewal' && (
          <button className={`btn btn-ghost btn-sm ${styles.alertBtn}`} onClick={onRenewalRequest} disabled={renewLoading}>
            <FiRefreshCw size={12} /> {renewLoading ? 'Opening...' : 'Renew Subscription'}
          </button>
        )}
      </div>
    )}
  </>
);

// -- DerasarEditForm -----------------------------------------------------------
const DerasarEditForm = ({ form, setForm, addrForm, setAddrForm, saving, onSave, onCancel }) => {
  const fc = (key) => (e) => setForm(f => ({ ...f, [key]: e.target.value }));
  const ac = (key) => (e) => setAddrForm(f => ({ ...f, [key]: e.target.value }));
  return (
    <>
      <div className="form-group">
        <label>Derasar Name</label>
        <input value={form.name} onChange={fc('name')} />
      </div>
      <StateCitySelect
        state={form.state}
        city={form.city}
        onStateChange={(val) => setForm((f) => ({ ...f, state: val }))}
        onCityChange={(val) => setForm((f) => ({ ...f, city: val }))}
      />
      <div className="form-group">
        <label>Description</label>
        <textarea rows={2} value={form.description} onChange={fc('description')} />
      </div>
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16 }}>
        <div className="form-group">
          <label>Establish Date</label>
          <input type="date" value={form.establishDate} onChange={fc('establishDate')} />
        </div>
      </div>
      <div className="form-group" style={{ marginTop: -16 }}>
        <label>Admin Notes</label>
        <textarea rows={2} value={form.notes} onChange={fc('notes')} placeholder="Internal notes for this Derasar..." />
      </div>

      {/* Address Details */}
      <div style={{ borderTop: '1px solid var(--color-border)', margin: '8px 0 16px', paddingTop: 16 }}>
        <div style={{ fontSize: '0.78rem', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.05em', color: 'var(--color-text-muted)', marginBottom: 14 }}>
          <FiMapPin size={12} style={{ marginRight: 5 }} />Address Details
        </div>
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16 }}>
          <div className="form-group">
            <label>Flat / Bunglow No.</label>
            <input value={addrForm.flatNo} onChange={ac('flatNo')} placeholder="e.g. A-101" />
          </div>
          <div className="form-group">
            <label>Apartment / Bunglow Name</label>
            <input value={addrForm.buildingName} onChange={ac('buildingName')} placeholder="e.g. Shanti Niketan" />
          </div>
          <div className="form-group" style={{ gridColumn: '1 / -1' }}>
            <label>Address Line 1</label>
            <input value={addrForm.addressLine1} onChange={ac('addressLine1')} placeholder="Street / Area / Locality" />
          </div>
          <div className="form-group" style={{ gridColumn: '1 / -1' }}>
            <label>Address Line 2</label>
            <input value={addrForm.addressLine2} onChange={ac('addressLine2')} placeholder="Landmark / Additional info" />
          </div>
          <StateCitySelect
            state={addrForm.state}
            city={addrForm.city}
            onStateChange={(val) => setAddrForm((f) => ({ ...f, state: val }))}
            onCityChange={(val) => setAddrForm((f) => ({ ...f, city: val }))}
          />
          <div className="form-group">
            <label>Pincode</label>
            <input value={addrForm.pincode} onChange={ac('pincode')} placeholder="e.g. 380001" />
          </div>
          <div className="form-group">
            <label>Country</label>
            <input value={addrForm.country} onChange={ac('country')} placeholder="India" />
          </div>
        </div>
      </div>

      <div style={{ display: 'flex', gap: 10 }}>
        <button className="btn btn-primary" onClick={onSave} disabled={saving}>
          {saving ? 'Saving…' : 'Save Changes'}
        </button>
        <button className="btn btn-ghost" onClick={onCancel}>Cancel</button>
      </div>
    </>
  );
};

// -- DerasarInfoView -----------------------------------------------------------
const DerasarInfoView = ({ derasar, addrForm, isDerasarAdmin, expiringSoon, isExpired }) => {
  const addrParts = [
    addrForm.flatNo && addrForm.buildingName
      ? `${addrForm.flatNo}, ${addrForm.buildingName}`
      : (addrForm.flatNo || addrForm.buildingName),
    addrForm.addressLine1, addrForm.addressLine2,
    addrForm.city, addrForm.state,
    addrForm.pincode,
    addrForm.country && addrForm.country !== 'India' ? addrForm.country : '',
  ].filter(Boolean);

  return (
    <>
      <div className={styles.derasarHeader}>
        <div>
          <h3 className={styles.derasarName}>{derasar.name}</h3>
          <div className={styles.derasarLocation}><FiMapPin size={13} /> {[derasar.city, derasar.state].filter(Boolean).join(', ')}</div>
          {derasar.description && <p className={styles.derasarDesc}>{derasar.description}</p>}
          {derasar.establishDate && (
            <div className={styles.derasarLocation} style={{ marginTop: 4 }}>
              <FiCalendar size={13} /> Established: {formatDate(derasar.establishDate + 'T00:00:00')}
            </div>
          )}
          {isDerasarAdmin && derasar.notes && (
            <p className={styles.derasarDesc} style={{ background: '#f9fafb', padding: '8px 12px', borderLeft: '3px solid var(--color-primary)', marginTop: 8 }}>
              <strong>Notes:</strong> {derasar.notes}
            </p>
          )}
          {isDerasarAdmin && addrParts.length > 0 && (
            <div className={styles.derasarLocation} style={{ marginTop: 6 }}>
              <FiMapPin size={13} /> {addrParts.join(', ')}
            </div>
          )}
        </div>
        <span className={subBadgeClass(derasar.subscriptionStatus)}>
          {subLabel[derasar.subscriptionStatus] || derasar.subscriptionStatus}
        </span>
      </div>

      <div className={styles.statsGrid}>
        <div className={styles.statCard}>
          <div className={styles.statIcon}><FiUsers size={18} /></div>
          <div className={styles.statNum}>{derasar.members?.length || 0}</div>
          <div className={styles.statLabel}>Total Members</div>
        </div>
        <div className={styles.statCard}>
          <div className={styles.statIcon}><FiUsers size={18} /></div>
          <div className={styles.statNum}>{derasar.personCount || 0}</div>
          <div className={styles.statLabel}>Total Persons</div>
        </div>
        <div className={styles.statCard}>
          <div className={styles.statIcon}><FiCalendar size={18} /></div>
          <div className={styles.statNum}>{derasar.startDate ? formatDate(derasar.startDate) : '—'}</div>
          <div className={styles.statLabel}>Active Since</div>
        </div>
        <div className={styles.statCard}>
          <div className={styles.statIcon}><FiCalendar size={18} /></div>
          <div className={styles.statNum}>{derasar.registeredSince ? formatDate(derasar.registeredSince) : '—'}</div>
          <div className={styles.statLabel}>Registered Since</div>
        </div>
        <div className={styles.statCard}>
          <div className={`${styles.statIcon} ${isExpired ? styles.iconDanger : expiringSoon ? styles.iconWarn : ''}`}>
            <FiCalendar size={18} />
          </div>
          <div className={`${styles.statNum} ${isExpired ? styles.textDanger : expiringSoon ? styles.textWarn : ''}`}>
            {derasar.endDate ? formatDate(derasar.endDate) : '—'}
          </div>
          <div className={styles.statLabel}>Valid Until</div>
        </div>
      </div>

    </>
  );
};

// -- Main Component ------------------------------------------------------------
const MyDerasar = () => {
  const { activeDerasarId, isDerasarAdmin, user, refreshUser } = useAuth();
  const navigate = useNavigate();

  const [derasar, setDerasar]     = useState(null);
  const [loading, setLoading]     = useState(true);
  const [editMode, setEditMode]   = useState(false);
  const [saving, setSaving]       = useState(false);
  const [actionLoading, setActionLoading] = useState(null);
  const [renewLoading, setRenewLoading]   = useState(false);

  const [form, setForm] = useState({
    name: '', city: '', state: '', description: '', establishDate: '', notes: '',
  });
  const [addrForm, setAddrForm] = useState({
    flatNo: '', buildingName: '', addressLine1: '', addressLine2: '',
    city: '', state: '', pincode: '', country: 'India',
  });
  const [stats, setStats] = useState(null);

  const fetchDerasar = () => {
    if (!activeDerasarId) return;
    setLoading(true);
    Promise.all([
      getDerasar(activeDerasarId),
      getUserProfile().catch(() => ({ data: {} })),
      getDashboard({ derasarId: activeDerasarId }).catch(() => null),
    ])
      .then(([derasarRes, profileRes, statsRes]) => {
        const d = derasarRes.data;
        setDerasar(d);
        setForm({
          name: d.name, city: d.city, state: d.state || '',
          description: d.description || '', establishDate: d.establishDate || '', notes: d.notes || '',
        });
        const p = profileRes.data || {};
        setAddrForm({
          flatNo: p.flatNo || '', buildingName: p.buildingName || '',
          addressLine1: p.addressLine1 || '', addressLine2: p.addressLine2 || '',
          city: p.city || '', state: p.state || '',
          pincode: p.pincode || '', country: p.country || 'India',
        });
        setStats(statsRes || null);
      })
      .catch(() => toast.error('Failed to load derasar'))
      .finally(() => setLoading(false));
  };

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { fetchDerasar(); }, [activeDerasarId]);

  const handleSave = async () => {
    setSaving(true);
    try {
      await Promise.all([updateDerasar(activeDerasarId, form), updateUserProfile(addrForm)]);
      await refreshUser();
      toast.success('Derasar updated');
      setEditMode(false);
      fetchDerasar();
    } catch (err) {
      toast.error(err.response?.data?.message || 'Failed to update');
    } finally {
      setSaving(false);
    }
  };

  const handleApprove = async (userId, action) => {
    setActionLoading(userId);
    try {
      await approveDerasarMember(activeDerasarId, userId, action);
      toast.success(`Member ${action}d`);
      fetchDerasar();
    } catch {
      toast.error('Failed to update member');
    } finally {
      setActionLoading(null);
    }
  };

  const handleRequestRenewal = async () => {
    setRenewLoading(true);
    navigate('/subscription/renew');
    setRenewLoading(false);
  };

  if (!activeDerasarId) return <div className="empty-state"><p>No active derasar selected</p></div>;
  if (loading) return <div className="loading"><div className="spinner" /></div>;
  if (!derasar)  return <div className="empty-state"><p>Derasar not found</p></div>;

  const days         = getDaysUntilExpiry(derasar.endDate);
  const expiringSoon = days !== null && days >= 0 && days <= 30;
  const isExpired    = days !== null && days < 0;

  return (
    <div className={styles.container}>
      <div className="page-header">
        <h2>My Derasar</h2>
        {isDerasarAdmin && !editMode && !isExpired && (
          <button className="btn btn-ghost" onClick={() => setEditMode(true)}>
            <FiEdit2 size={14} /> Edit
          </button>
        )}
      </div>

      {isDerasarAdmin && (
        <AlertBanners
          derasar={derasar} days={days}
          expiringSoon={expiringSoon} isExpired={isExpired}
          onRenewalRequest={handleRequestRenewal} renewLoading={renewLoading}
        />
      )}

      <div className={styles.infoCard}>
        <div className={`${styles.cardAccent} ${isExpired ? styles.accentExpired : expiringSoon ? styles.accentWarning : styles.accentPrimary}`} />
        <div className={styles.cardContent}>
          {editMode ? (
            <DerasarEditForm
              form={form} setForm={setForm}
              addrForm={addrForm} setAddrForm={setAddrForm}
              saving={saving}
              onSave={handleSave}
              onCancel={() => setEditMode(false)}
            />
          ) : (
            <DerasarInfoView
              derasar={derasar} addrForm={addrForm}
              isDerasarAdmin={isDerasarAdmin}
              expiringSoon={expiringSoon} isExpired={isExpired}
            />
          )}

          {stats && !editMode && (
            <div className="stats-grid" style={{ marginTop: 20 }}>
              <div className="stat-card">
                <div className="stat-icon orange"><FiActivity /></div>
                <div className="stat-info">
                  <h4>{fmtCurrency(stats.totalDonations || 0)}</h4>
                  <p>{isDerasarAdmin ? 'Total Donations' : 'Your Total Donations'}</p>
                </div>
              </div>
              <div className="stat-card">
                <div className="stat-icon green"><FiCheckCircle /></div>
                <div className="stat-info">
                  <h4>{fmtCurrency(stats.paidAmount || 0)}</h4>
                  <p>{isDerasarAdmin ? 'Collected' : 'Donated'}</p>
                </div>
              </div>
              <div className="stat-card">
                <div className="stat-icon red"><FiClock /></div>
                <div className="stat-info">
                  <h4>{fmtCurrency(stats.pendingAmount || 0)}</h4>
                  <p>Pending</p>
                </div>
              </div>
              {isDerasarAdmin && (
                <div className="stat-card">
                  <div className="stat-icon blue"><FiGift /></div>
                  <div className="stat-info">
                    <h4>{fmtCurrency(stats.anonymousAmount || 0)}</h4>
                    <p>Shah A/C Donations</p>
                  </div>
                </div>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default MyDerasar;
